import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "akademika/akamont/pages/sharedControllers/BaseController";
import { Routes } from "akademika/akamont/constants/Routes";
import {
  GetUserRoleAccess,
  IsUserInternal,
} from "akademika/akamont/services/AuthService";
import { GetStateForWorkOrderStatus } from "akademika/akamont/utils/WorkOrderStatusUtil";
import { Specification } from "akademika/akamont/models/Specification";
import { SpecificationWorkOrderListFilters } from "akademika/akamont/models/SpecificationWorkOrderListFilters";
import {
  GetButtonTypeForSpecificationStatus,
  GetStateForSpecificationStatus,
  GetTextKeyForSpecificationStatusButton,
} from "../../utils/SpecificationStatusUtil";
import {
  onSelectWorkOrder,
  onSelectAllWorkOrders,
  updateMainContractNumber,
} from "../../utils/SpecificationSelectWorkOrderUtil";
import Event from "sap/ui/base/Event";
import { ConfirmDialogData } from "../../models/ConfirmDialogData";
import GetSpecificationChangeStatusDialogData from "../../utils/GetSpecificationChangeStatusData";
import { SpecificationStatus } from "../../models/SpecificationStatuses";
import { ValueState } from "sap/ui/core/library";
import Tokenizer from "sap/m/Tokenizer";

/**
 * @namespace akademika.akamont.pages.specificationDetails
 */
export default class SpecificationDetails extends BaseController {
  route = Routes.SPECIFICATION_DETAILS;
  parentRoute = Routes.SPECIFICATION_LIST;
  workOrdersTableId = "workOrdersTable";

  updateSpecificationUri = "api/Specification/UpdateSpecification";
  getSpecificationDetailsUri = "api/Specification/GetSpecificationDetails/{id}";
  getWorkOrdersUri = "api/Specification/GetWorkOrders";
  getNextSpecificatoinStatusesUri =
    "api/Specification/GetNextSpecificationStatuses/{id}";
  postChangeSpecificatoinStatusUri =
    "api/Specification/ChangeSpecificationStatus";
  deleteSpecificationUri = "api/Specification/DeleteSpecification/{id}";
  id = "0";

  public async onRouteMatched() {
    this.id = this.routeParams.id;

    this.setModel(new JSONModel(GetUserRoleAccess()), "userRoleAccess");
    this.setModel(new JSONModel([]), "workOrders");
    this.setModel(new JSONModel({ selectAll: false }), "workOrdersOptions");

    await this.getSpecificationDetails();
  }

  public async applyWorkOrderFilters() {
    const model = this.getModel();
    const specification: Specification = model.getData();

    const filters = {
      specificationId: this.id,
      partnerId: specification.partnerId,
      plannedDate: specification.date,
    } as SpecificationWorkOrderListFilters;

    const workOrdersListResponse = await this.post<Record<string, unknown>[]>(
      this.getWorkOrdersUri,
      {
        body: filters,
      }
    );

    const exitingWrokOrderIds = workOrdersListResponse.map(
      (wo) => wo["workOrderId"]
    );
    specification.workOrderIds =
      specification.workOrderIds?.filter((woId) =>
        exitingWrokOrderIds.includes(woId)
      ) || [];

    specification.totalAmount = 0;
    workOrdersListResponse.forEach((wo) => {
      if (
        wo["workOrderId"] &&
        specification.workOrderIds?.includes(wo["workOrderId"] as number)
      ) {
        wo["selected"] = true;
        specification.totalAmount =
          (specification.totalAmount || 0) + ((wo["amount"] as number) || 0);
      } else {
        wo["selected"] = false;
      }
    });

    this.setModel(new JSONModel(workOrdersListResponse), "workOrders");
    model.setData(specification);
  }

  private async getSpecificationDetails() {
    const nextStatusesUrl = this.getNextSpecificatoinStatusesUri.replace(
      "{id}",
      this.id
    );
    this.fetchModel(nextStatusesUrl, "nextStatuses");

    // Clean up the contracts list
    const contractsNameList = this.byId("contractsNameList") as Tokenizer;
    contractsNameList.removeAllTokens();

    const url = this.getSpecificationDetailsUri.replace("{id}", this.id);
    await this.fetchModel<Specification>(url);
    const model = this.getModel();
    const specification: Specification = model.getData();

    await this.applyWorkOrderFilters();

    const workOrdersModel = this.getModel("workOrders");
    const workOrdersData: Record<string, unknown>[] = workOrdersModel.getData();

    specification.totalAmount = 0;
    workOrdersData?.forEach((wo) => {
      if (
        wo["workOrderId"] &&
        specification.workOrderIds?.includes(wo["workOrderId"] as number)
      ) {
        wo["selected"] = true;
        specification.totalAmount =
          (specification.totalAmount || 0) + ((wo["amount"] as number) || 0);
        updateMainContractNumber(this, wo, true);
      } else {
        wo["selected"] = false;
        this.setModel(new JSONModel({ selectAll: false }), "workOrdersOptions");
        updateMainContractNumber(this, wo, false);
      }
    });

    model.setData(specification);
    workOrdersModel.setData(workOrdersData);

    this.setModel(new JSONModel({ edit: false }), "viewOptions");
  }

  public async onUpdatepecification() {
    this.hideMessage();

    const specification = this.getModelData<Specification>();
    await this.post(this.updateSpecificationUri, {
      body: specification,
    });

    this.getSpecificationDetails();
  }

  public async onDeleteSpecification() {
    this.openSimpleDialog({
      title: "deleteSpecification",
      message: "deleteSpecificationQuestion",
      state: ValueState.Warning,
      type: "Confirm",
      confirmWithComment: false,
      confirmAction: async () => {
        const uri = this.deleteSpecificationUri.replace("{id}", this.id);
        await this.post(uri);
        this.navTo(this.parentRoute);
      },
      cancelAction: async () => this.getSpecificationDetails(),
    });
  }

  public async onEditSpecification() {
    this.hideMessage();
    const viewOptionsModel = this.getModel("viewOptions") as JSONModel;
    let editValue = viewOptionsModel.getProperty("/edit");
    viewOptionsModel.setProperty("/edit", !editValue);

    if (editValue) {
      await this.getSpecificationDetails();
    }
  }

  public onSelectAllWorkOrders(event: Event) {
    onSelectAllWorkOrders(this, event);
  }

  public onSelectWorkOrder() {
    onSelectWorkOrder(this);
  }

  public getStateForWorkOrderStatus = GetStateForWorkOrderStatus;
  public getStateForSpecificationStatus = GetStateForSpecificationStatus;
  public getButtonTypeForSpecificationStatus =
    GetButtonTypeForSpecificationStatus;

  public formatTextForStatusButton(status: string) {
    return this.translate(GetTextKeyForSpecificationStatusButton(status));
  }

  public async changeStatus(newStatus: SpecificationStatus) {
    const confirmDialogData: ConfirmDialogData =
      GetSpecificationChangeStatusDialogData(newStatus);

    this.openSimpleDialog({
      title: confirmDialogData.title,
      message: confirmDialogData.message,
      state: confirmDialogData.dialogState,
      type: "Confirm",
      confirmWithComment: false,
      confirmAction: async () => {
        await this.saveDataAndShowMessage(
          this.postChangeSpecificatoinStatusUri,
          {
            params: {
              specificationId: this.id,
              newStatus: newStatus,
            },
          }
        );
        await this.getSpecificationDetails();
      },
      cancelAction: async () => await this.getSpecificationDetails(),
    });
  }

  public async onWorkOrderDetails(workOrderId: number) {
    const targetRoute = IsUserInternal()
      ? Routes.WORK_ORDER_DETAILS_INTERNAL
      : Routes.WORK_ORDER_DETAILS_PARTNER;

    this.navTo(targetRoute, { workOrderId });
  }

  public onExportPdf() {
    this.navTo(Routes.SPECIFICATION_TO_PDF, { specificationId: this.id });
  }

  public isEnabledStatusButton(
    status: string,
    workOrderIds: number[],
    isEdit: any
  ) {
    if (isEdit) return false;

    const hasSelected = workOrderIds ? workOrderIds.length : false;
    return !(status === "finished" && !hasSelected);
  }
}
